import * as R from 'ramda'
import {arrayOf, shape} from 'prop-types'
import {graphql} from 'gatsby'
// MUI v7 removed the legacy `<Grid item xs md>` API (items now use `size={}`),
// which silently made every item full-width and stacked the cards under the
// filters. GridLegacy is MUI's drop-in for the v5 Grid, restoring the two-column
// layout without rewriting the JSX below.
import {GridLegacy as Grid} from '@mui/material'
import loadable from '@loadable/component'
import React from 'react'

import {
  FilterPropTypes,
  FiltersContainerPropTypes,
  keyplaysPropTypes,
  locationPropTypes,
  pageContextPropTypes,
  partnerHeaderPropTypes,
  playbookHeaderPropTypes,
} from 'helpers/propTypes'

import Filter from 'components/UI/Filter'
import FiltersContainer from 'components/UI/FiltersContainer'
import Keyplays from 'components/PlayBook/Keyplays'
import PartnersFilter from 'components/PlayBook/Filters/PartnersFilter'
import Section from 'components/UI/Section'
import SEO from 'components/seo'
import SwitchHeader from 'components/PlayBook/SwitchHeader'
import useHashConverter from 'hooks/useHashConverter'
import useHashReader from 'hooks/useHashReader'
import useIsMobile from 'hooks/useIsMobile'
import usePartnerHeader from 'hooks/usePartnerHeader'
import usePlaybookFiltersData from 'hooks/usePlaybookFiltersData'
import useTagsObject from 'hooks/useTagsObject'

const PlayBook = ({pageContext, data, location}) => {
  const countryData = R.path(['contentfulCountry'], data)
  const Decoration = loadable(() => import('components/UI/Decoration'))
  const Decorations = loadable(() => import('components/UI/Decorations'))

  const {
    metaTitle,
    metaDescription,
    pageTitle,
    partnersShortDescription,
    searchPlaceholder,
    filterByIndustryLabel,
    filterByDepartmentLabel,
    childContentfulPlaybookShortDescriptionTextNode,
    filterByPartnerLabel,
    filterByServiceLabel,
    filterSeeMoreLabel,
    filterSeeLessLabel,
    eulaLink,
  } = R.pathOr(null, ['allContentfulPlaybook', 'edges', 0, 'node'], data)

  const keyplays = R.pathOr(null, ['allContentfulKeyPlay', 'nodes'], data)

  const partnersHeaderTitle = R.pathOr(
    '',
    ['allContentfulMenuItem', 'edges', 0, 'node', 'title'],
    data,
  )

  const {
    allTagsObjects,
    newTagsPartnersObjects,
    newTagsServicesObjects,
    newTagsDepartmentsObjects,
    newTagsIndustriesObjects,
    newAllPartnersObjects,
    allPartnersToggleTag,
    services,
  } = usePlaybookFiltersData(data)

  const isMobile = useIsMobile()
  const [hashConditions] = useHashReader(location)
  const [selectedTags] = useHashConverter()
  const [selectedTagsObjects] = useTagsObject(selectedTags, allTagsObjects)
  const [partnerHeaderContent] = usePartnerHeader(
    newAllPartnersObjects,
    selectedTags,
  )

  const [currentServicesTags] = useTagsObject(
    selectedTags,
    newTagsServicesObjects,
  )
  const [currentIndustriesTags] = useTagsObject(
    selectedTags,
    newTagsIndustriesObjects,
  )
  const [currentDepartmentsTags] = useTagsObject(
    selectedTags,
    newTagsDepartmentsObjects,
  )
  const [currentPartnersTags] = useTagsObject(
    selectedTags,
    newTagsPartnersObjects,
  )

  const pageMetaData = {
    metaTitle: R.pathOr('', ['metaTitle'], metaTitle) || metaTitle,
    metaDescription: R.pathOr('', ['metaDescription'], metaDescription),
    siteMetaData: R.pathOr('', ['siteMetaData'], data),
    genericData: R.pathOr('', ['contentfulGenericData'], data),
  }

  return (
    <>
      <SEO
        pageContext={pageContext}
        countryData={countryData}
        pageMetaData={pageMetaData}
      />
      <SwitchHeader
        partner={partnerHeaderContent}
        pageTitle={pageTitle}
        description={childContentfulPlaybookShortDescriptionTextNode}
        partnersTitle={partnersHeaderTitle}
        partnersShortDescription={partnersShortDescription}
        hashConditions={hashConditions}
        eulaLink={eulaLink}
        countryData={countryData}
      />
      <Decorations>
        <Decoration webSiteName="keyrus" color="orange" right={0} top={130} />
        <Decoration webSiteName="keyrus" color="blue" left={0} top={154} />
      </Decorations>
      <Section hasPaddingTop={false}>
        <Grid
          container
          spacing={1}
          style={isMobile ? {marginTop: 0} : {marginTop: 70}}
        >
          <Grid item xs={12} sm={6} md={4} style={{padding: 0}}>
            <FiltersContainer searchPlaceholder={searchPlaceholder} hasBorder>
              {services && (
                <Filter
                  currentTags={currentServicesTags}
                  filterLabel={filterByServiceLabel}
                  filterSeeMoreLabel={filterSeeMoreLabel}
                  filterSeeLessLabel={filterSeeLessLabel}
                  tags={newTagsServicesObjects}
                  hasObjectsTags
                />
              )}
              <Filter
                currentTags={currentIndustriesTags}
                filterLabel={filterByIndustryLabel}
                filterSeeMoreLabel={filterSeeMoreLabel}
                filterSeeLessLabel={filterSeeLessLabel}
                tags={newTagsIndustriesObjects}
                hasObjectsTags
              />
              <Filter
                currentTags={currentDepartmentsTags}
                filterLabel={filterByDepartmentLabel}
                filterSeeMoreLabel={filterSeeMoreLabel}
                filterSeeLessLabel={filterSeeLessLabel}
                tags={newTagsDepartmentsObjects}
                hasObjectsTags
              />
              <PartnersFilter
                currentTags={currentPartnersTags}
                filterByPartnerLabel={filterByPartnerLabel}
                filterByPartnerDefaultFilter={allPartnersToggleTag[0]}
                filterSeeMoreLabel={filterSeeMoreLabel}
                filterSeeLessLabel={filterSeeLessLabel}
                tags={newTagsPartnersObjects}
                hasObjectsTags
              />
            </FiltersContainer>
          </Grid>
          <Grid item xs={12} sm={6} md={8} style={{padding: 0}}>
            <Keyplays
              allContentfulPartner={newAllPartnersObjects}
              keyplays={keyplays}
              currentTags={selectedTagsObjects}
              filterSeeMoreLabel={filterSeeMoreLabel}
              hashConditions={hashConditions}
            />
          </Grid>
        </Grid>
      </Section>
    </>
  )
}

PlayBook.propTypes = {
  data: shape({
    allContentfulpage: shape({
      edges: arrayOf(
        shape({
          node: shape({
            header: playbookHeaderPropTypes,
            partnerHeader: partnerHeaderPropTypes,
            filtersContainer: FiltersContainerPropTypes,
            filter: FilterPropTypes,
            keyplays: keyplaysPropTypes,
          }),
        }),
      ),
    }),
  }).isRequired,
  location: locationPropTypes.isRequired,
  pageContext: pageContextPropTypes.isRequired,
}

PlayBook.defaultProps = {}

export const pageQuery = graphql`
  query templatePlaybook(
    $entityRegEx: String
    $nodeLocale: String
    $technicalName: String
  ) {
    allContentfulPlaybook(
      filter: {node_locale: {eq: $nodeLocale}, slug: {regex: $entityRegEx}}
    ) {
      edges {
        node {
          ...playBookFields
        }
      }
    }
    allContentfulMenuItem(
      filter: {name: {eq: "Partners"}, node_locale: {eq: $nodeLocale}}
    ) {
      edges {
        node {
          title
        }
      }
    }
    allContentfulIndustry(filter: {node_locale: {eq: $nodeLocale}}) {
      edges {
        node {
          name {
            name
          }
          key_play {
            id
          }
        }
      }
    }
    allContentfulDepartment(filter: {node_locale: {eq: $nodeLocale}}) {
      edges {
        node {
          name {
            name
          }
          key_play {
            id
          }
        }
      }
    }
    allContentfulService(filter: {node_locale: {eq: $nodeLocale}}) {
      edges {
        node {
          ...serviceFields
        }
      }
    }
    allContentfulKeyPlay(
      filter: {
        node_locale: {eq: $nodeLocale}
        countriesOfDestination: {
          elemMatch: {technicalName: {eq: $technicalName}}
        }
      }
    ) {
      nodes {
        ...playbookKeyPlayFields
      }
    }
    contentfulCountry(
      node_locale: {eq: $nodeLocale}
      technicalName: {eq: $technicalName}
    ) {
      id
      name
      ...countryDataFields
      ...partnerFields
      services {
        topLevelServices {
          entryId
          subLevelServices {
            entryId
          }
        }
      }
    }
    siteMetaData: site {
      ...SiteMetadata
    }
    contentfulGenericData {
      genericKeywords {
        content
      }
    }
  }
`

export default PlayBook
